import React, { useState, useRef, useEffect } from 'react';
import {
  Camera,
  HelpCircle,
  Trash2,
  Move,
  Upload,
  Plus,
  X,
  Check,
  Settings,
  Grid,
  AlignCenter,
  Smartphone,
  Image as ImageIcon,
} from 'lucide-react';

interface ContentElement {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  content?: string;
  style?: React.CSSProperties;
}

interface MobileDevice {
  id: string;
  name: string;
  width: number;
  height: number;
  frame: {
    width: number;
    height: number;
    image: string;
    screen: {
      x: number;
      y: number;
      width: number;
      height: number;
      radius: number;
    };
  };
}

const MobileContentSimulator: React.FC = () => {
  const [contentElements, setContentElements] = useState<ContentElement[]>([]);
  const [selectedElement, setSelectedElement] = useState<ContentElement | null>(null);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [configElement, setConfigElement] = useState<ContentElement | null>(null);
  const [moveMode, setMoveMode] = useState(false);
  const [showGuides, setShowGuides] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [gridSize, setGridSize] = useState(20);
  const [selectedDevice, setSelectedDevice] = useState('iphone14');
  const [newImageUrl, setNewImageUrl] = useState('');

  const availableImages = [
    'https://via.placeholder.com/400x400/4F46E5/FFFFFF?text=Image+1',
    'https://via.placeholder.com/400x400/7C3AED/FFFFFF?text=Image+2',
    'https://via.placeholder.com/400x400/10B981/FFFFFF?text=Image+3',
  ];

  const mobileDevices: MobileDevice[] = [
    {
      id: 'iphone14',
      name: 'iPhone 14 Pro',
      width: 375,
      height: 812,
      frame: {
        width: 428,
        height: 926,
        image: 'https://www.iphoneframe.live/static/iphone14pro.png',
        screen: {
          x: 19,
          y: 93,
          width: 390,
          height: 844,
          radius: 54,
        },
      },
    },
    {
      id: 'iphone13',
      name: 'iPhone 13',
      width: 390,
      height: 844,
      frame: {
        width: 428,
        height: 926,
        image: 'https://www.iphoneframe.live/static/iphone13.png',
        screen: {
          x: 19,
          y: 93,
          width: 390,
          height: 844,
          radius: 54,
        },
      },
    },
    // Add more devices as needed
  ];

  const mobileDisplayRef = useRef<HTMLDivElement>(null);

  // Add a new content element
  const addContentElement = (type: string, content: string = '') => {
    const newElement: ContentElement = {
      id: Date.now().toString(),
      type,
      x: 20,
      y: 20,
      width: 200,
      height: type === 'text' ? 100 : 200,
      content,
      style: {},
    };
    setContentElements([...contentElements, newElement]);
  };

  // Handle element selection
  const handleElementClick = (element: ContentElement, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedElement(element);
  };

  // Handle mobile display click (deselect element)
  const handleMobileDisplayClick = () => {
    setSelectedElement(null);
  };

  // Delete selected element
  const deleteSelectedElement = () => {
    if (!selectedElement) return;
    setContentElements(contentElements.filter(el => el.id !== selectedElement.id));
    setSelectedElement(null);
  };

  // Render the mobile display with content elements
  const renderMobileDisplay = () => {
    const device = mobileDevices.find(d => d.id === selectedDevice) || mobileDevices[0];
    const scale = 0.7; // Adjust scale as needed

    return (
      <div
        className="relative mx-auto"
        style={{
          width: device.frame.width * scale,
          height: device.frame.height * scale,
          backgroundImage: `url(${device.frame.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        <div
          ref={mobileDisplayRef}
          className="absolute overflow-hidden bg-white"
          style={{
            left: device.frame.screen.x * scale,
            top: device.frame.screen.y * scale,
            width: device.frame.screen.width * scale,
            height: device.frame.screen.height * scale,
            borderRadius: device.frame.screen.radius * scale,
            position: 'relative',
          }}
          onClick={handleMobileDisplayClick}
        >
          {/* Content elements */}
          {contentElements.map(element => (
            <div
              key={element.id}
              className={`absolute border-2 ${
                selectedElement?.id === element.id
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : 'border-transparent hover:border-blue-300'
              }`}
              style={{
                left: `${element.x}px`,
                top: `${element.y}px`,
                width: `${element.width}px`,
                height: `${element.height}px`,
                ...element.style,
              }}
              onClick={e => handleElementClick(element, e)}
            >
              {element.type === 'text' ? (
                <div className="p-2 w-full h-full overflow-auto">
                  {element.content || 'Double click to edit...'}
                </div>
              ) : (
                <img src={element.content} alt="Content" className="w-full h-full object-cover" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Device selector */}
      <div className="flex items-center space-x-4 mb-4">
        <label className="font-medium">Device:</label>
        <select
          className="border rounded px-3 py-1"
          value={selectedDevice}
          onChange={e => setSelectedDevice(e.target.value)}
        >
          {mobileDevices.map(device => (
            <option key={device.id} value={device.id}>
              {device.name}
            </option>
          ))}
        </select>
      </div>

      {/* Main content */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Mobile display */}
        <div className="flex-1">{renderMobileDisplay()}</div>

        {/* Controls */}
        <div className="w-full lg:w-64 bg-gray-50 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-4">Add Elements</h2>

          <div className="space-y-2">
            <button
              className="w-full flex items-center space-x-2 bg-white border rounded p-2 hover:bg-gray-50"
              onClick={() => addContentElement('text')}
            >
              <AlignCenter size={16} />
              <span>Add Text</span>
            </button>

            <button
              className="w-full flex items-center space-x-2 bg-white border rounded p-2 hover:bg-gray-50"
              onClick={() => setShowImagePicker(true)}
            >
              <ImageIcon size={16} />
              <span>Add Image</span>
            </button>

            <div className="pt-4 mt-4 border-t">
              <h3 className="font-medium mb-2">Tools</h3>
              <div className="space-y-2">
                <button
                  className={`w-full flex items-center space-x-2 p-2 rounded ${
                    moveMode ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
                  }`}
                  onClick={() => setMoveMode(!moveMode)}
                >
                  <Move size={16} />
                  <span>{moveMode ? 'Moving...' : 'Move Elements'}</span>
                </button>

                <button
                  className="w-full flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                  onClick={() => setShowGuides(!showGuides)}
                >
                  <Grid size={16} />
                  <span>{showGuides ? 'Hide Guides' : 'Show Guides'}</span>
                </button>

                {selectedElement && (
                  <button
                    className="w-full flex items-center space-x-2 p-2 text-red-600 hover:bg-red-50 rounded"
                    onClick={deleteSelectedElement}
                  >
                    <Trash2 size={16} />
                    <span>Delete Selected</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Picker Modal */}
      {showImagePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Add Image</h3>
              <button onClick={() => setShowImagePicker(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              {availableImages.map((imageUrl, index) => (
                <div
                  key={index}
                  className="border rounded overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500"
                  onClick={() => {
                    addContentElement('image', imageUrl);
                    setShowImagePicker(false);
                  }}
                >
                  <img
                    src={imageUrl}
                    alt={`Image ${index + 1}`}
                    className="w-full h-32 object-cover"
                  />
                </div>
              ))}
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Or enter image URL:
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  className="flex-1 border rounded px-3 py-2"
                  placeholder="https://example.com/image.jpg"
                  value={newImageUrl}
                  onChange={e => setNewImageUrl(e.target.value)}
                />
                <button
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  onClick={() => {
                    if (newImageUrl) {
                      addContentElement('image', newImageUrl);
                      setShowImagePicker(false);
                      setNewImageUrl('');
                    }
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileContentSimulator;
